import React from 'react';
import { UnifiedModal } from './UnifiedModal';
import { UnifiedButton } from './UnifiedButton';
import { UnifiedInput, UnifiedTextarea } from './UnifiedInput';

/**
 * UnifiedInputDialog — canonical "we need one or more values from you" dialog.
 *
 * Replaces the sprinkled \`window.prompt()\` calls (which are blocking, one
 * field only, no styling, no validation feedback) with a single, themed,
 * multi-field prompt that speaks the platform's design language.
 *
 * Props:
 *  - open, onClose
 *  - title, description
 *  - fields: Array<{
 *      name: string,
 *      label: string,
 *      type?: 'text' | 'number' | 'password' | 'textarea',
 *      defaultValue?: string,
 *      placeholder?: string,
 *      helperText?: string,
 *      required?: boolean,
 *      validate?: (value: string, all: Record<string, string>) => string | null,
 *      min?: number, max?: number,  // for number type
 *    }>
 *  - onSubmit: async (values: Record<string, string>) => void
 *      — throw / reject to keep the dialog open, resolve to close.
 *  - submitLabel, cancelLabel
 *  - loading — optional parent-controlled loading override
 *  - testId
 */
export const UnifiedInputDialog = ({
  open,
  onClose,
  title,
  description,
  fields = [],
  onSubmit,
  submitLabel = 'تأكيد',
  cancelLabel = 'إلغاء',
  loading: externalLoading,
  testId = 'unified-input',
}) => {
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [pending, setPending] = React.useState(false);
  const busy = externalLoading ?? pending;

  // Reset when the dialog opens so re-opens don't leak previous input.
  React.useEffect(() => {
    if (open) {
      const initial = {};
      for (const f of fields) initial[f.name] = f.defaultValue ?? '';
      setValues(initial);
      setErrors({});
    }
  }, [open]);

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: null }));
  };

  const runValidation = () => {
    const nextErrors = {};
    for (const f of fields) {
      const v = values[f.name] ?? '';
      if (f.required && !String(v).trim()) {
        nextErrors[f.name] = 'هذا الحقل مطلوب';
        continue;
      }
      if (f.validate) {
        const err = f.validate(String(v), values);
        if (err) nextErrors[f.name] = err;
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!runValidation()) return;
    if (externalLoading !== undefined) {
      onSubmit?.(values);
      return;
    }
    setPending(true);
    try {
      await onSubmit?.(values);
    } finally {
      setPending(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={busy ? undefined : onClose}
      size="sm"
      title={title}
      testId={testId}
      footer={(
        <>
          <UnifiedButton
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            testId={`${testId}-cancel`}
          >
            {cancelLabel}
          </UnifiedButton>
          <UnifiedButton
            variant="primary"
            onClick={handleSubmit}
            disabled={busy}
            testId={`${testId}-submit`}
          >
            {busy ? 'جارٍ الحفظ…' : submitLabel}
          </UnifiedButton>
        </>
      )}
    >
      {description && (
        <p
          style={{
            margin: 0,
            marginBottom: 'var(--mesp-space-4)',
            fontSize: 'var(--mesp-font-sm)',
            color: 'var(--mesp-text-muted)',
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mesp-space-4)' }}>
        {fields.map((f) => {
          const Field = f.type === 'textarea' ? UnifiedTextarea : UnifiedInput;
          const commonProps = {
            label: f.label,
            value: values[f.name] ?? '',
            onChange: (e) => setField(f.name, e.target.value),
            placeholder: f.placeholder,
            error: errors[f.name],
            helperText: f.helperText,
            disabled: busy,
            testId: `${testId}-field-${f.name}`,
          };
          if (f.type === 'textarea') {
            return <Field key={f.name} {...commonProps} rows={f.rows || 3} />;
          }
          return (
            <Field
              key={f.name}
              {...commonProps}
              type={f.type || 'text'}
              min={f.min}
              max={f.max}
            />
          );
        })}
      </div>
    </UnifiedModal>
  );
};

export default UnifiedInputDialog;
